import { useParams, Link } from "react-router-dom";
import { useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { Tabs } from "@mantine/core";
import { gql } from "@/__generated__";
import { SurveyTag } from "shared/src/data/survey-tags";
import Navbar from "@/components/layout/Navbar/Navbar";
import { ReferPatientProgressTracker } from "@/components/ReferPatientProgressTracker";
import { StrengthenReferral } from "../StrengthenReferral/StrengthenReferral";
import { PageFooter } from "@/components/layout/PageFooter";
import {
  FilteredSurveyTab,
  FilteredSurveyTabHandle,
} from "../StrengthenReferral/components/tabs/FilteredSurveyTab/FilteredSurveyTab";
import styles from "./medical-info.module.scss";
import { flushSync } from "react-dom";
import { useFormContext } from "@/contexts/FormContext";

const GET_APPEAL_STATUS = gql(`
  query GetAppealStatusForDiagnosis($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
      status {
        referred
      }
    }
  }
`);

export function Diagnosis() {
  const { appealId } = useParams<{ appealId: string }>();
  const surveyRef = useRef<FilteredSurveyTabHandle>(null);
  const { resetDirty } = useFormContext();
  const [isSaving, setIsSaving] = useState(false);

  const { data, loading: statusLoading } = useQuery(GET_APPEAL_STATUS, {
    variables: { appealId: appealId! },
    skip: !appealId,
    fetchPolicy: "no-cache",
  });

  const readOnly = data?.getAppealById?.status?.referred ?? false;

  if (!appealId) {
    return null;
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const success = await surveyRef.current?.save();
      if (success) {
        flushSync(() => {
          resetDirty();
        });
      }
      return success ?? false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBeforeSend = async () => {
    return await handleSave();
  };

  const footer =
    !statusLoading && !readOnly ? (
      <PageFooter
        primaryButton={{
          text: "Save",
          onClick: handleSave,
          loading: isSaving,
          disabled: isSaving,
        }}
      />
    ) : undefined;

  const tabContent = (
    <div className={styles.container}>
      <div className={styles.sideTabsContainer}>
        <Tabs
          value="diagnosis"
          orientation="vertical"
          className={styles.sideTabs}
        >
          <Tabs.List>
            <Tabs.Tab
              value="diagnosis"
              leftSection={
                <i className={`fas fa-circle-plus ${styles.tabIcon}`} />
              }
            >
              <Link
                to={`/appeals/${appealId}/strengthen/medical-info/diagnosis`}
              >
                Diagnosis
              </Link>
            </Tabs.Tab>
            <Tabs.Tab
              value="medication-history"
              leftSection={
                <i className={`fas fa-circle-plus ${styles.tabIcon}`} />
              }
            >
              <Link
                to={`/appeals/${appealId}/strengthen/medical-info/medication-history`}
              >
                Medication History
              </Link>
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </div>
      <div className={styles.contentContainer}>
        <FilteredSurveyTab
          ref={surveyRef}
          appealId={appealId}
          readOnly={readOnly}
          filterTag={SurveyTag.DIAGNOSIS}
        />
      </div>
    </div>
  );

  return (
    <Navbar
      titleComponent={
        <ReferPatientProgressTracker
          appealId={appealId}
          onBeforeSendReferral={handleSaveBeforeSend}
        />
      }
      footer={footer}
    >
      <StrengthenReferral
        appealId={appealId}
        activeTab="medical-info"
        tabContent={tabContent}
      />
    </Navbar>
  );
}
